import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  getHomepageBootstrapPromise,
  getPublicHomeBootstrap,
  prefetchHomepageBootstrap,
} from '@/utils/publicBootstrap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let homepageCache = null;
let homepageInflight = null;

export function seedHomepagePageConfig(config) {
  homepageCache = config;
  homepageInflight = null;
}

export function getHomepagePageConfigCache() {
  return homepageCache;
}

export function usePageConfig({ isHomepage = false, slug = null, pageId = null } = {}) {
  const initialHomepage = isHomepage ? (homepageCache || getPublicHomeBootstrap()?.page_config || null) : null;
  const [config, setConfig] = useState(initialHomepage);
  const [loading, setLoading] = useState(isHomepage ? !initialHomepage : true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const finish = (nextConfig, nextError = null) => {
      if (cancelled) return;
      setConfig(nextConfig);
      setError(nextError);
      setLoading(false);
    };

    const loadHomepage = async () => {
      const cached = homepageCache || getPublicHomeBootstrap()?.page_config;
      if (cached) {
        finish(cached);
        return;
      }

      const bootstrapPromise = getHomepageBootstrapPromise() || prefetchHomepageBootstrap();
      if (bootstrapPromise) {
        const bootstrap = await bootstrapPromise;
        if (bootstrap?.page_config) {
          finish(bootstrap.page_config);
          return;
        }
      }

      if (!homepageInflight) {
        homepageInflight = axios
          .get(`${API}/site-settings/page-config/homepage`)
          .then((res) => {
            homepageCache = res.data;
            return res.data;
          })
          .catch((e) => {
            throw e;
          })
          .finally(() => {
            homepageInflight = null;
          });
      }

      try {
        const data = await homepageInflight;
        finish(data);
      } catch (e) {
        finish(null, e?.response?.status === 404 ? 'NOT_FOUND' : 'ERROR');
      }
    };

    const loadOther = async () => {
      setLoading(true);
      setError(null);
      try {
        let url;
        if (slug) url = `${API}/site-settings/page-config/by-slug/${encodeURIComponent(slug)}`;
        else if (pageId) url = `${API}/site-settings/page-config/${pageId}`;
        else throw new Error('Missing page identifier');

        const res = await axios.get(url);
        finish(res.data);
      } catch (e) {
        finish(null, e?.response?.status === 404 ? 'NOT_FOUND' : 'ERROR');
      }
    };

    if (isHomepage) {
      loadHomepage();
    } else {
      loadOther();
    }

    return () => { cancelled = true; };
  }, [isHomepage, slug, pageId]);

  return { config, loading, error };
}

export default usePageConfig;
